import mixpanel, { Config } from 'mixpanel-browser';
import { mixPanelEvents } from './events';

interface UserInfo {
  emailId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface MixpanelProperties {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

class MixPanelService {
  private static instance: MixPanelService | null = null;
  private static projectToken: string | undefined = import.meta.env
    .VITE_MIXPANEL_PROJECT_TOKEN;
  private static options: Partial<Config> = {
    debug: import.meta.env.VITE_MIXPANEL_DEBUG_STATE === 'true',
  };
  private userInfo: UserInfo | null = null;

  private constructor() {}

  public static getInstance(): MixPanelService {
    if (!MixPanelService.instance) {
      MixPanelService.instance = new MixPanelService();
    }
    return MixPanelService.instance;
  }

  private logError(error: unknown): void {
    console.error('BOOM : MIXPANEL ERROR');
    console.error(error);
  }

  public init(userInfo?: UserInfo): void {
    try {
      if (MixPanelService.projectToken) {
        mixpanel.init(MixPanelService.projectToken, MixPanelService.options);
      }
      if (userInfo?.emailId) {
        this.identify(userInfo.emailId);
      }
    } catch (error) {
      this.logError(error);
    }
  }

  public track(eventName: string, properties?: MixpanelProperties): void {
    try {
      if (this.userInfo?.emailId) {
        this.identify(this.userInfo.emailId);
      }
      mixpanel.track(eventName, properties || {});
    } catch (error) {
      this.logError(error);
    }
  }

  public register(properties: MixpanelProperties, days: number = 365): void {
    try {
      mixpanel.register(properties, days);
    } catch (error) {
      this.logError(error);
    }
  }

  public registerOnce(
    properties: MixpanelProperties,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    defaultValue: any,
    days: number = 365
  ): void {
    try {
      mixpanel.register_once(properties, defaultValue, days);
    } catch (error) {
      this.logError(error);
    }
  }

  public alias(aliasName: string, originalName?: string): void {
    try {
      mixpanel.alias(aliasName, originalName);
    } catch (error) {
      this.logError(error);
    }
  }

  public identify(uniqueId: string): void {
    try {
      mixpanel.identify(uniqueId);
    } catch (error) {
      this.logError(error);
    }
  }

  public setUser(userInfo: MixpanelProperties): void {
    try {
      if (this.userInfo?.emailId) {
        this.identify(this.userInfo.emailId);
      }
      mixpanel.people.set(userInfo);
    } catch (error) {
      this.logError(error);
    }
  }

  public setUserPropsOnce(props: MixpanelProperties): void {
    try {
      if (this.userInfo?.emailId) {
        this.identify(this.userInfo.emailId);
      }
      mixpanel.people.set_once(props);
    } catch (error) {
      this.logError(error);
    }
  }

  public appendUserProps(
    props: MixpanelProperties,
    allowDuplicate: boolean
  ): void {
    try {
      if (this.userInfo?.emailId) {
        this.identify(this.userInfo.emailId);
      }
      if (allowDuplicate) {
        mixpanel.people.append(props);
      } else {
        mixpanel.people.union(props);
      }
    } catch (error) {
      this.logError(error);
    }
  }

  public removeUserProps(props: MixpanelProperties): void {
    try {
      if (this.userInfo?.emailId) {
        this.identify(this.userInfo.emailId);
      }
      mixpanel.people.remove(props);
    } catch (error) {
      this.logError(error);
    }
  }

  public incrementUserProps(propsInfo: MixpanelProperties): void {
    try {
      if (this.userInfo?.emailId) {
        this.identify(this.userInfo.emailId);
      }
      mixpanel.people.increment(propsInfo);
    } catch (error) {
      this.logError(error);
    }
  }

  public firePageViewEvent(pageName?: string): void {
    try {
      setTimeout(() => {
        const docPageName = pageName || document.title || '';
        if (docPageName)
          this.register({
            'Page Name': docPageName,
          });
        this.track(mixPanelEvents.VIEW_PAGE);
      }, 1000);
    } catch (error) {
      this.logError(error);
    }
  }

  public fireErrorEvent(error: string): void {
    try {
      this.track(mixPanelEvents.Error, {
        error,
      });
    } catch (error) {
      this.logError(error);
    }
  }

  public resetAfterSignOut(): void {
    try {
      mixpanel.reset();
    } catch (error) {
      this.logError(error);
    }
  }
}

export const mixPanelServiceInstance = MixPanelService.getInstance();
